import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/ProtocolConsumptionSyncAnalysis.Service';

// models
import {
	SyncAnalysis,
	ISyncAnalysisFindingsFilters,
} from '../../models/Entities/Protocols/SyncAnalysis';
import { ErrorType } from '../../models/HttpError';

// actions
import * as Actions from '../actions';

export { SYNC_ANALYSIS_FINDINGS_DEFAULT_PAGE_SIZE } from '../../services/ProtocolConsumptionSyncAnalysis.Service';

type KnownAction =
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisGetRequestAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisGetSuccessAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisGetFailureAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisGetFindingsRequestAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisGetFindingsSuccessAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisGetFindingsFailureAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisExecuteRequestAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisExecuteSuccessAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisExecuteFailureAction
	| Actions.ProtocolConsumptionSyncAnalysis.SyncAnalysisRefreshSuccessAction;

const Unauthorized: ErrorType = {
	ErrorCode: 401,
	ErrorMessage: 'Not authorized',
	Errors: [],
};

const GetProtocolConsumptionSyncAnalysis = (
	syncProcessId: string
): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) {
		dispatch({ type: 'SYNCANALYSIS_GET_FAILURE', syncProcessId, error: Unauthorized });
		return;
	}

	dispatch({ type: 'SYNCANALYSIS_GET_REQUEST', syncProcessId });

	try {
		const analysis: SyncAnalysis | null = await Services.GetProtocolConsumptionSyncAnalysis(
			syncProcessId,
			token
		);
		dispatch({ type: 'SYNCANALYSIS_GET_SUCCESS', syncProcessId, analysis });
	} catch (error) {
		dispatch({ type: 'SYNCANALYSIS_GET_FAILURE', syncProcessId, error: error as ErrorType });
	}
};

const GetProtocolConsumptionSyncAnalysisFindings = (
	analysisId: string,
	pageNumber?: number,
	pageSize?: number,
	filters?: ISyncAnalysisFindingsFilters
): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) {
		dispatch({ type: 'SYNCANALYSIS_GETFINDINGS_FAILURE', analysisId, error: Unauthorized });
		return;
	}

	dispatch({ type: 'SYNCANALYSIS_GETFINDINGS_REQUEST', analysisId });

	try {
		const page = await Services.GetProtocolConsumptionSyncAnalysisFindings(
			analysisId,
			token,
			pageNumber,
			pageSize,
			filters
		);
		dispatch({
			type: 'SYNCANALYSIS_GETFINDINGS_SUCCESS',
			analysisId,
			findings: page.findings,
			paginationState: page.paginationState,
		});
	} catch (error) {
		dispatch({ type: 'SYNCANALYSIS_GETFINDINGS_FAILURE', analysisId, error: error as ErrorType });
	}
};

/**
 * Relee el análisis sin anunciarlo. Es la lectura del sondeo mientras el análisis
 * corre: el servidor lo persiste en curso apenas arranca, así que releerlo es lo
 * que hace que el panel se actualice solo hasta que termina.
 *
 * Un fallo suelto no se muestra ni se registra: el análisis sigue corriendo del
 * lado del servidor y la próxima lectura probablemente lo alcance. Pintar un error
 * por cada bache de red convertiría el sondeo en una fuente de ruido.
 */
const RefreshProtocolConsumptionSyncAnalysis = (
	syncProcessId: string
): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) return;

	try {
		const analysis: SyncAnalysis | null = await Services.GetProtocolConsumptionSyncAnalysis(
			syncProcessId,
			token
		);
		dispatch({ type: 'SYNCANALYSIS_REFRESH_SUCCESS', syncProcessId, analysis });
	} catch {
		// Ver el comentario de arriba: el sondeo sigue.
	}
};

/**
 * Dispara el análisis a demanda sobre una corrida ya existente.
 *
 * A diferencia del disparo automático, este corre aunque la verificación previa no
 * haya encontrado indicios: el usuario puede querer revisar igual.
 *
 * Termine bien o mal, el análisis queda persistido —un fallo se guarda con su
 * motivo—, así que en los dos casos se relee para que el panel muestre lo que
 * quedó registrado y no el estado anterior. La relectura va ANTES de marcar la
 * falla: `SYNCANALYSIS_GET_REQUEST` limpia `error`, y al revés se perdería el
 * motivo del fallo.
 */
const ExecuteProtocolConsumptionSyncAnalysis = (
	syncProcessId: string
): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) {
		dispatch({ type: 'SYNCANALYSIS_EXECUTE_FAILURE', syncProcessId, error: Unauthorized });
		return;
	}

	dispatch({ type: 'SYNCANALYSIS_EXECUTE_REQUEST', syncProcessId });

	// La relectura del análisis: es la misma en los dos desenlaces. Su propia falla
	// no se propaga como falla de la ejecución, que es un hecho distinto.
	const reload = async () => {
		dispatch({ type: 'SYNCANALYSIS_GET_REQUEST', syncProcessId });
		try {
			const analysis: SyncAnalysis | null = await Services.GetProtocolConsumptionSyncAnalysis(
				syncProcessId,
				token
			);
			dispatch({ type: 'SYNCANALYSIS_GET_SUCCESS', syncProcessId, analysis });
		} catch (error) {
			dispatch({ type: 'SYNCANALYSIS_GET_FAILURE', syncProcessId, error: error as ErrorType });
		}
	};

	try {
		const analysisId: string = await Services.ExecuteProtocolConsumptionSyncAnalysis(
			syncProcessId,
			token
		);
		await reload();
		dispatch({ type: 'SYNCANALYSIS_EXECUTE_SUCCESS', syncProcessId, analysisId });
	} catch (error) {
		await reload();
		dispatch({ type: 'SYNCANALYSIS_EXECUTE_FAILURE', syncProcessId, error: error as ErrorType });
	}
};

export const ActionCreators = {
	GetProtocolConsumptionSyncAnalysis,
	GetProtocolConsumptionSyncAnalysisFindings,
	ExecuteProtocolConsumptionSyncAnalysis,
	RefreshProtocolConsumptionSyncAnalysis,
};
